import { Component, ElementRef, HostListener, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatInput, MatPrefix, MatSuffix } from '@angular/material/input'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { debounceTime, distinctUntilChanged, map, Observable, Subject, switchMap, tap } from 'rxjs'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'
import { AsyncPipe } from '@angular/common'
import { MatIconModule } from '@angular/material/icon'
import { MeetingFilters, FiltersComponent } from '../filters/filters.component'
import { anyFiltersApplied, Meeting, MeetingsService, MetricType } from '../meetings.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { MeetingListComponent } from '../meeting-list/meeting-list.component'
import { MatIconButton } from '@angular/material/button'
import { isEqual } from '../utils'
import { useMediaTimePrecision } from '../media-player-controller/media-player-controller.component'
import { MetricsService } from '../metrics.service'

@Component({
  selector: 'app-search-bar',
  standalone: true,
  imports: [
    FormsModule,
    MatInput,
    TranslatePipe,
    TranslateDirective,
    AsyncPipe,
    MatFormField,
    MatIconModule,
    MatPrefix,
    FiltersComponent,
    SpinnerComponent,
    MeetingListComponent,
    MatIconButton,
    MatSuffix,
    RouterLink,
  ],
  templateUrl: './search-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './search-panel.component.scss',
})
export class SearchPanelComponent implements OnInit {
  @ViewChild('queryInput')
  queryInputComponent!: ElementRef

  router = inject(Router)
  route = inject(ActivatedRoute)
  meetingsService = inject(MeetingsService)
  metricsService = inject(MetricsService)
  query$ = this.route.queryParams.pipe(
    map((params) => params['filterQuery'] ?? ''),
    distinctUntilChanged()
  )
  isOpen$ = this.route.queryParams.pipe(
    map((params) => params['isSearchOpen'] === 'true'),
    tap(() => this.setFocusToInput()),
    distinctUntilChanged()
  )
  filters$: Observable<MeetingFilters> = this.route.queryParamMap.pipe(
    map((queryParams) => {
      const showOnlyPastMeetingsParam = queryParams.get('filterShowOnlyPastMeetings')
      return {
        query: queryParams.get('filterQuery') ?? null,
        guestOrganizationId: typeof queryParams.get('filterGuestOrganizationId') === 'string' ? parseInt(queryParams.get('filterGuestOrganizationId')!) : null,
        attendeeId: queryParams.get('filterAttendeeId') ?? null,
        speakerIds: queryParams.getAll('filterSpeakerIds') ?? [],
        startDate: typeof queryParams.get('filterStartDate') === 'string' ? new Date(queryParams.get('filterStartDate')!) : null,
        endDate: typeof queryParams.get('filterEndDate') === 'string' ? new Date(queryParams.get('filterEndDate')!) : null,
        showOnlyPastMeetings: showOnlyPastMeetingsParam === null ? true : showOnlyPastMeetingsParam === 'true',
      }
    }),
    distinctUntilChanged((previous, current) => isEqual(previous, current))
  )
  results$ = this.filters$.pipe(
    tap(() => (this.chosenMeeting = null)),
    switchMap((filters) => {
      return this.meetingsService.search(filters)
    })
  )
  queryInput$ = new Subject<string | null>()
  showFilters = true
  chosenMeeting: Meeting | null = null
  activeMatch: string | null = null

  ngOnInit() {
    this.queryInput$.pipe(debounceTime(500)).subscribe((query) => {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { filterQuery: query },
        queryParamsHandling: 'merge',
      })
    })
    this.meetingsService.prefetchSearchFilterOptions()
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.setIsOpen(false)
    }
  }

  handleClickOnSearchResultSnippet() {
    this.metricsService.sendMetric(null, MetricType.CLICK_ON_TEXT_SNIPPET_IN_SEARCH_RESULTS)
  }

  handleClickOnSearchResultMeeting() {
    this.metricsService.sendMetric(null, MetricType.CLICK_ON_MEETING_IN_SEARCH_RESULTS)
  }

  setIsOpen(isOpen: boolean) {
    const value = isOpen ? true : null
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { isSearchOpen: value },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  anyFiltersApplied = anyFiltersApplied

  private setFocusToInput() {
    setTimeout(() => this.queryInputComponent.nativeElement.focus(), 0)
  }

  resetFilters() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        filterQuery: null,
        filterGuestOrganizationId: null,
        filterAttendeeId: null,
        filterSpeakerIds: null,
        filterStartDate: null,
        filterEndDate: null,
        filterShowOnlyPastMeetings: null,
      },
      queryParamsHandling: 'merge',
    })
  }

  toMediaTimePrecision(time: number): number {
    return useMediaTimePrecision(time)
  }
}
